// @ts-nocheck
/**
 * primitiveRenderers.ts — Template V2 M3 Primitive Component Renderers
 *
 * Exports typed ComponentRenderer implementations for:
 *   TEXT_RENDERER    — 'text'
 *   NUMBER_RENDERER  — 'number'
 *   BOOLEAN_RENDERER — 'boolean'
 *   DATE_RENDERER    — 'date'
 *
 * All renderers:
 *  - Use extractTypedProps() for safe, cast-free prop access (DEC-020).
 *  - Apply DEC-019 fail-open via applyExpressionFallback().
 *  - Resolve values from static literals or pre-resolved binding values.
 *  - Emit no React/RN imports; output is a plain serializable RenderDescriptor.
 */

import { componentInteractionProps } from './interactionProps';
import { applyExpressionFallback, buildDescriptor, coerceResolvedDisplayValue, resolveValue } from './rendererUtils';
import type { CompiledPropsByType, ComponentRenderer } from './types';
import { extractTypedProps } from './types';

/**
 * ItemDefinition keys stored as unix-ms. Bound text components must format as
 * datetime (not raw digits) — same contract as editor `inputType: 'timestamp'`.
 */
const TIMESTAMP_ITEM_FIELD_VALUE_KINDS = new Set<string>([
  'createdAt',
  'procuredAt',
  'loanedAt',
]);

// ── Text ──────────────────────────────────────────────────────────────────────

export const TEXT_RENDERER: ComponentRenderer<CompiledPropsByType['text']> = {
  supportedType: 'text',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('text', context.node.props);
    const value = coerceResolvedDisplayValue(resolveValue(p.text.source, context), context.locale);
    const hrefRaw = p.text.href ? resolveValue(p.text.href, context) : null;
    const href =
      typeof hrefRaw === 'string' && hrefRaw.trim().length > 0 ? hrefRaw.trim() : null;
    let valueKind: 'datetime' | undefined;
    let format = p.text.format ?? null;
    const readRef = context.node.binding?.read;
    if (
      readRef
      && typeof readRef === 'object'
      && 'kind' in readRef
      && readRef.kind === 'categoryProperty'
    ) {
      const category = context.compiledTemplate.categoriesById[readRef.categoryId];
      const property = category?.properties.find((prop) => prop.id === readRef.propertyId);
      if (property?.type === 'datetime' || property?.type === 'date') {
        valueKind = 'datetime';
        // Category `date` is calendar-day storage; default display to date (not datetime).
        if (property.type === 'date' && !format?.dateFormat) {
          format = { ...(format ?? {}), dateFormat: 'date' };
        }
      }
    } else if (
      readRef
      && typeof readRef === 'object'
      && 'kind' in readRef
      && readRef.kind === 'itemField'
      && TIMESTAMP_ITEM_FIELD_VALUE_KINDS.has(readRef.field)
    ) {
      valueKind = 'datetime';
    }
    return buildDescriptor(context.node, visible, enabled, {
      value: value ?? null,
      href,
      format,
      valueKind,
      editable: p.text.editable ?? false,
      placeholder: p.text.placeholder ?? null,
      multiline: p.text.multiline ?? false,
      maxLines: p.text.maxLines ?? null,
      overflow: p.text.overflow ?? null,
      ...componentInteractionProps(context.node),
    }, { diagnostics: context.issues.slice(issuesBefore), renderContext: context });
  },
};

// ── Number ────────────────────────────────────────────────────────────────────

export const NUMBER_RENDERER: ComponentRenderer<CompiledPropsByType['number']> = {
  supportedType: 'number',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('number', context.node.props);
    const value = resolveValue(p.number.source, context);
    return buildDescriptor(context.node, visible, enabled, {
      value: value ?? null,
      format: p.number.format ?? null,
      maxDigits: p.number.maxDigits ?? null,
      editable: p.number.editable ?? false,
      placeholder: p.number.placeholder ?? null,
      ...componentInteractionProps(context.node),
    }, { diagnostics: context.issues.slice(issuesBefore), renderContext: context });
  },
};

// ── Boolean ───────────────────────────────────────────────────────────────────

export const BOOLEAN_RENDERER: ComponentRenderer<CompiledPropsByType['boolean']> = {
  supportedType: 'boolean',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('boolean', context.node.props);
    const value = resolveValue(p.boolean.source, context);
    const resolveOptionalColor = (source: typeof p.boolean.trueColor): string | null => {
      if (source == null) return null;
      const raw = resolveValue(source, context);
      return typeof raw === 'string' && raw.trim().length > 0 ? raw : null;
    };
    return buildDescriptor(context.node, visible, enabled, {
      value: value ?? null,
      trueLabel: p.boolean.trueLabel ?? null,
      falseLabel: p.boolean.falseLabel ?? null,
      variant: p.boolean.variant ?? 'checkbox',
      labelPlacement: p.boolean.labelPlacement ?? 'trailing',
      trueColor: resolveOptionalColor(p.boolean.trueColor),
      falseColor: resolveOptionalColor(p.boolean.falseColor),
      outlineColor: resolveOptionalColor(p.boolean.outlineColor),
      editable: p.boolean.editable ?? false,
      ...componentInteractionProps(context.node),
    }, { diagnostics: context.issues.slice(issuesBefore), renderContext: context });
  },
};

// ── Date ──────────────────────────────────────────────────────────────────────

export const DATE_RENDERER: ComponentRenderer<CompiledPropsByType['date']> = {
  supportedType: 'date',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('date', context.node.props);
    const value = resolveValue(p.date.source, context);
    return buildDescriptor(context.node, visible, enabled, {
      value: value ?? null,
      format: p.date.format ?? null,
      editable: p.date.editable ?? false,
      pickerMode: p.date.pickerMode ?? 'datetime',
      ...componentInteractionProps(context.node),
    }, { diagnostics: context.issues.slice(issuesBefore), renderContext: context });
  },
};
